import { useCallback, useState } from "react";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { useManutencaoEquipObraPagination } from "../hooks/Obras_maquinasequipamentos/pagination";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useLocation } from "react-router";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { useObrasTopHeaderState } from "../components/hooks/obras_top_header_state";
import { Obra } from "../../domain/models/obra";
import { useObrasCommonReports } from "../components/hooks/obras_common_reports";
import { TelasIds } from "../../../../common/utils/tela_ids";
import { ManutencaoEquipObra } from "../../domain/models/manutencao_equip_obra";
import { ManutencaoEquipObraLista } from "../../domain/models/manutencao_equip_obra_lista";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { ManutencaoEquipObraForm } from "../schemas/Obras_maquinasequipamentos/Obras_maquinasequipamentos";

type LoadManutencaoEquipObraFuncState = {
  idObra?: number,

  page?: number,
  rowsPerPage?: number,
}

type LoadObraFuncState = {
  idObra?: number;

};

export const useObrasMaquinasEquipamentosViewModel = () => {
  const {
    obras: {
      getAllManutencaoEquipObraUseCase,
      getManutencaoEquipObraByIdUseCase,
      deleteManutencaoEquipObraUseCase,
      updateManutencaoEquipObraUseCase,
      createManutencaoEquipObraUseCase,
      getObraByIdUseCase,

    },
  } = useDependencyInjection();

  const backOrNavigate = useBackOrNavigate();
  const obrasTopHeaderState = useObrasTopHeaderState();
  const location = useLocation();
  const pagination = useManutencaoEquipObraPagination();
  const { pushErrorNotification, pushInfoNotification } = usePageLoader();

  const [idObra, setIdObra] = useState<number>();
  const [obrasReportState, initializeReportState] = useObrasCommonReports({ idObra });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [errorMessage, setErrorMessage] = useState<string>();
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const [selectedObra, setSelectedObra] = useState<Obra>();

  const [openCreateModal, setOpenCreateModal] = useState(false);

  const [selectedIdManutencaoEquipObra, setSelectedIdManutencaoEquipObra] = useState<number>();
  const [selectedManutencaoEquipObra, setSelectedManutencaoEquipObra] = useState<ManutencaoEquipObra | null>(null);
  const [manutencaoEquipObraLista, setManutencaoEquipObraLista] = useState<ManutencaoEquipObraLista[]>([]);




  //#region  CARREGAMENTO

  const loadObra = useCallback(
    async (funcState?: LoadObraFuncState) => {
      try {
        const filteredIdObra = funcState?.idObra ?? idObra;

        const obra = await getObraByIdUseCase.getObraById(
          TelasIds.Obras_equipamentosmedicao,
          filteredIdObra,
        );
        setSelectedObra(obra);
      } catch (e: unknown) {
        setSelectedObra(undefined);
        throw e;
      }
    },
    [idObra, getObraByIdUseCase]
  );

  const loadManutencaoEquipObraLista = useCallback(
    async (funcState?: LoadManutencaoEquipObraFuncState) => {
      try {
        const filteredIdObra = evaluateParamValue(funcState?.idObra, idObra);

        const filterpage = funcState?.page ?? pagination.page;
        const filterRowspage = funcState?.rowsPerPage ?? pagination.rowsPerPage;

        await getAllManutencaoEquipObraUseCase.execute(
          filteredIdObra ?? 0,
          filterpage,
          filterRowspage
        );

        setManutencaoEquipObraLista(getAllManutencaoEquipObraUseCase.items);

        pagination.setRowsPerPage(getAllManutencaoEquipObraUseCase.pageSize);
        pagination.setTotalItems(getAllManutencaoEquipObraUseCase.totalItems);
      } catch (e: unknown) {
        setManutencaoEquipObraLista([]);
        pushErrorNotification("Erro", "Falha ao buscar equipamentos de medição!");
      }
    }, [
    pagination,
    getAllManutencaoEquipObraUseCase,
    idObra,
    pushErrorNotification

  ]);


  const loadManutencaoEquipObraById = useCallback(async (idManutencaoEquipObra: number) => {
    setLoading(true);
    try {

      const response = await getManutencaoEquipObraByIdUseCase.execute(idManutencaoEquipObra);
      setSelectedManutencaoEquipObra(response);

    } catch (error) {
      pushErrorNotification("Erro", "Falha ao buscar dados do equipamento de medição!", { isAutoFinish: true });
    } finally {
      setLoading(false);
      setOpenDeleteModal(false);
    }
  }, [
    getManutencaoEquipObraByIdUseCase,
    pushErrorNotification,
  ]
  );

  //#endregion




  //#region PAGE START

  usePageStartBootstrap(async () => {
    try {
      const params = new URLSearchParams(location.search);
      const locationId = params.get("id");
      const id = locationId ?? undefined;

      if (!id || id.length === 0) {
        setErrorMessage("Falha ao recuperar id da obra!");
        return;
      }
      setIdObra(Number(id));
      await loadObra({ idObra: Number(id) });
      initializeReportState(Number(id));

      await loadManutencaoEquipObraLista({ idObra: Number(id) });
    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao buscar dados das paginas!")[0]);
    }
  });

  //#endregion

  //#region CADASTRO E LEITURA



  const handleManutencaoEquipObraCadastroOpenState = useCallback(async (idManutencaoEquipObra: number | null, openState: boolean) => {
    setOpenCreateModal(openState);

    if (openState && idManutencaoEquipObra != null) {
      if (idManutencaoEquipObra && manutencaoEquipObraLista) {
        manutencaoEquipObraLista.find((x) => x.idManutencaoEquipObra === idManutencaoEquipObra);
        await loadManutencaoEquipObraById(idManutencaoEquipObra);
      }
    } else {
      setSelectedManutencaoEquipObra(null);
    }
  },
    [
      manutencaoEquipObraLista,
      loadManutencaoEquipObraById,
      pushErrorNotification
    ]);


  const handleManutencaoEquipObraSubmit = useCallback(
    async (form: ManutencaoEquipObraForm) => {
      try {
        setSaving(true);
        if (!selectedManutencaoEquipObra) {
          await createManutencaoEquipObraUseCase.execute(
            idObra ?? 0,
            form.status,
            form.descricao ?? undefined,
            form.dataManutencao ?? undefined,
            form.dataProxManutencao ?? undefined,
            form.dataPrevDevolucao ?? undefined,
            form.dataDevolucao ?? undefined,
            form.parteEletrica ?? undefined,
            form.parteMecanica ?? undefined,
            form.lubrificacao ?? undefined,
            form.limpeza ?? undefined,
            form.observacao ?? undefined,
            form.outros1 ?? undefined,
            form.outros2 ?? undefined,
            undefined,
            form.idFornecedor ?? undefined,
            form.descLocatario ?? undefined,
            form.statusDevolucao ?? undefined,
          );
          pushInfoNotification("Sucesso", "Equipamento de medição criado com sucesso!");
        } else {
          if (form.idManutencaoEquipObra != null) {
            await updateManutencaoEquipObraUseCase.execute(
              form.idManutencaoEquipObra,
              idObra ?? 0,
              form.status,
              form.descricao ?? undefined,
              form.dataManutencao ?? undefined,
              form.dataProxManutencao ?? undefined,
              form.dataPrevDevolucao ?? undefined,
              form.dataDevolucao ?? undefined,
              form.parteEletrica ?? undefined,
              form.parteMecanica ?? undefined,
              form.lubrificacao ?? undefined,
              form.limpeza ?? undefined,
              form.observacao ?? undefined,
              form.outros1 ?? undefined,
              form.outros2 ?? undefined,
              undefined,
              form.idFornecedor ?? undefined,
              form.descLocatario ?? undefined,
              form.statusDevolucao ?? undefined,
            );
            pushInfoNotification("Sucesso", "Equipamento de medição atualizado com sucesso!");
          } else {
            pushErrorNotification("Atenção", "Id do equipamento de medição não encontrado para atualização!");
          }
        }
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao atualizar equipamento de medição!"));
      } finally {
        setSaving(false);
        setOpenCreateModal(false);
        setSelectedManutencaoEquipObra(null);
        await loadManutencaoEquipObraLista();
      }
    },
    [
      idObra,
      selectedManutencaoEquipObra,
      createManutencaoEquipObraUseCase,
      updateManutencaoEquipObraUseCase,
      loadManutencaoEquipObraLista,
      pushErrorNotification
    ]);


  //#endregion

  const handleBack = useCallback(() => {
    backOrNavigate(-1, { alternativeRoute: "/Obras_lista" });
  }, [backOrNavigate]);


  //#region DELETE

  const handleDeleteManutencaoEquipObraOpenState = useCallback(
    (idManutencaoEquipObra: number | null, openState: boolean) => {
      if (openState) {
        if (idManutencaoEquipObra != null) {
          setSelectedIdManutencaoEquipObra(idManutencaoEquipObra);
        } else {
          setSelectedIdManutencaoEquipObra(undefined);
        }
      }
      setOpenDeleteModal(openState);
    },
    [selectedIdManutencaoEquipObra]
  );

  const handleDeleteManutencaoEquipObraSubmit = useCallback(
    async () => {
      setLoading(true);
      if (selectedIdManutencaoEquipObra === undefined) return;
      try {
        await deleteManutencaoEquipObraUseCase.execute(selectedIdManutencaoEquipObra);
        await loadManutencaoEquipObraLista();
        pushInfoNotification("Sucesso", "Equipamento de medição deletado com sucesso!");
      } catch {
        pushErrorNotification("Erro", "Falha ao deletar o equipamento de medição!");
      } finally {
        setLoading(false);
        setOpenDeleteModal(false);
      }
    },
    [selectedIdManutencaoEquipObra,
      deleteManutencaoEquipObraUseCase,
      loadManutencaoEquipObraLista,
    ]);


  //#endregion



  //#region PAGINATION E PESQUISA

  const handleReload = useCallback(async () => {
    try {
      setLoading(true);
      await loadManutencaoEquipObraLista();
    } catch (e: unknown) {
      pushErrorNotification('Error', processError(e, 'Falha ao buscar lista de manutenção de equipamentos de obra!'));
    } finally {
      setLoading(false);
    }
  }, [loadManutencaoEquipObraLista, pushErrorNotification]);


  const handlePageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      pagination.setPage(page + 1);
      await loadManutencaoEquipObraLista({ page: page + 1 });
    } catch (e: unknown) {
      pushErrorNotification('Error', processError(e, 'Falha ao buscar lista de manutenção de equipamentos de obra!'));
    } finally {
      setLoading(false);
    }
  }, [pagination, loadManutencaoEquipObraLista, pushErrorNotification]);


  const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
      setLoading(true);
      pagination.setPage(1);
      pagination.setRowsPerPage(rowsPerPage);
      await loadManutencaoEquipObraLista({ rowsPerPage, page: 1 });
    } catch (e: unknown) {
      pushErrorNotification('Error', processError(e, 'Falha ao buscar lista de manutenção de equipamentos de obra!'));
    } finally {
      setLoading(false);
    }
  }, [pagination, loadManutencaoEquipObraLista, pushErrorNotification]);


  //#endregion




  return {
    idObra,
    selectedObra,
    obrasReportState,
    manutencaoEquipObraLista: manutencaoEquipObraLista ?? [],
    loading,
    saving,
    errorMessage,
    openDeleteModal,
    openCreateModal,
    selectedManutencaoEquipObra,
    obrasTopHeaderState,
    pagination,

    handleBack,
    handleManutencaoEquipObraCadastroOpenState,
    handleManutencaoEquipObraSubmit,
    handleDeleteManutencaoEquipObraOpenState,
    handleDeleteManutencaoEquipObraSubmit,
    handleReload,
    handlePageChange,
    handleRowsPerPageChange,

  };
};
